import { useCallback, useMemo } from 'react';

import isEqual from 'lodash/isEqual';
import { StringParam, useQueryParam } from 'use-query-params';

export interface SortObject {
  desc: boolean;
  id: string;
}

export type SetSortingFn = (newSort: SortObject[]) => void;

export interface UseSortingOptions {
  defaultSort?: SortObject[];
}

const SEPARATOR = ',';

function sortingStateToQueryString(
  sort: SortObject[] | undefined,
): string | undefined {
  const queryString = (sort || [])
    .map(({ id, desc }) => `${desc && id ? '-' : ''}${id}`)
    .join(SEPARATOR);

  return queryString || undefined;
}

const queryStringToSortingState = (
  sortString: string | undefined | null,
): SortObject[] => {
  return (sortString || '').split(SEPARATOR).map<SortObject>((item) => {
    const isDescending = item.startsWith('-');

    return {
      desc: isDescending,
      id: isDescending ? item.substring(1) : item,
    };
  });
};

const EMPTY_SORT: SortObject[] = [];

export function useSorting(options?: UseSortingOptions) {
  const [queryParam, setQueryParam] = useQueryParam('sort', StringParam);
  const defaultSort = options?.defaultSort ?? EMPTY_SORT;

  const sorting = useMemo<SortObject[]>(() => {
    if (queryParam) {
      return queryStringToSortingState(queryParam);
    }

    return defaultSort;
  }, [queryParam, defaultSort]);

  const setSorting = useCallback<SetSortingFn>(
    (newSort) => {
      // if newSort is [] and current sorting is the same as defaultSort,
      // we want to set it with the opposite order of defaultSort
      // Otherwise, `sorting` value is stuck with defaultSort
      if (
        newSort.length === 0 &&
        defaultSort.length > 0 &&
        isEqual(sorting, defaultSort)
      ) {
        const oppositeDefaultSort = defaultSort.map((item) => ({
          ...item,
          desc: !item.desc,
        }));

        return setQueryParam(sortingStateToQueryString(oppositeDefaultSort));
      }

      return setQueryParam(
        sortingStateToQueryString(newSort.length === 0 ? defaultSort : newSort),
      );
    },
    [setQueryParam, sorting, defaultSort],
  );

  return {
    setSorting,
    sortString: sortingStateToQueryString(sorting),
    sorting,
  };
}
